import { Component, computed, ElementRef, inject, input, InputSignal, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SetSelectedRequestItemId } from '../../../../../state/client-requests.actions';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Avatar, DATA_SELECTOR_INJECTION_TOKEN, getAvatarByFullName } from '@aiwyn/shared';
import { getTimeElapsedString } from 'src/app/shared/utils';
import { LaunchDarklyService } from 'src/app/config/services/launch-darkly.service';
import { LaunchDarklyFlag } from 'src/app/config/state';
import { ClientRequestsState } from '../../../../../state';
import { ClientRequestItemActivityViewModel } from '../../../../../models';

@Component({
  selector: 'awn-activity-item',
  templateUrl: './activity-item.component.html',
  styleUrl: './activity-item.component.scss',
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'activity-item',
    },
  ],
  standalone: false,
})
export class ActivityItemComponent implements OnChanges {
  answerActivity: InputSignal<ClientRequestItemActivityViewModel> = input();
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  avatar!: Avatar;
  dateStatus!: string;

  @ViewChild('activityBlock') activityBlockElement: ElementRef<HTMLElement>;

  isFileFlaggingEnabled = toSignal(this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_FILE_FLAGGING), {
    initialValue: false,
  });

  // Get flagged files for this specific request item from the activity's file data
  flaggedFiles = computed(() => {
    if (!this.isFileFlaggingEnabled() || !this.answerActivity() || this.answerActivity().activityType !== 'document') {
      return [];
    }

    const activity = this.answerActivity();

    // Only document items will have files
    if (!activity.files) {
      return [];
    }

    return activity.files
      .filter((file) => file.is_flagged === true)
      .map((file) => ({
        id: file.id,
        name: file.name,
        isFlagged: file.is_flagged,
      }));
  });

  isActive$: Observable<boolean> = this.store.select(ClientRequestsState.getSlices.selectedRequestItemId).pipe(
    map((requestItemId) => requestItemId === this.answerActivity().itemId),
    tap((isActive) => {
      if (isActive) {
        this.activityBlockElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }),
  );

  constructor(
    private store: Store,
    private _launchDarklyService: LaunchDarklyService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { answerActivity } = changes;

    if (answerActivity && this.answerActivity()) {
      this.avatar = getAvatarByFullName(this.answerActivity().userId || -1, this.answerActivity().userName || 'Unknown User');
      this.dateStatus = getTimeElapsedString(this.answerActivity().rejectedAt);
    } else if (!this.answerActivity()) {
      this.avatar = null;
      this.dateStatus = null;
    }
  }

  selectRequestItem(): void {
    this.store.dispatch(new SetSelectedRequestItemId(this.answerActivity().itemId));
  }
}
